import GameState from "../enum/gamestate";
import Game from "../models/game";
import { ApiGame } from "../types/apigame";

enum GameScore {
    WaitingForOtherPlayer = 0,
    TeammatesTurn,
    Live,
    NudgeWillCauseGameAction,
    SeeEndOfGame,
    NeedsMyAction,
}

/**
 * Sort by:
 *  Needs my action
 *  Is a complete game and I want to see the ending
 *  I can nudge someone and they surrender
 *  Is live
 *  Is my teammate's turn
 *  Everything else
 */
function getGameScore(game: ApiGame, userId: number): number {
    const isMyTurn = game.activeSeats.some(
        (seatObject) => seatObject.userId === userId,
    );

    const isInvited = game.invites?.some(
        (invite) => invite.user?.id === userId,
    );

    if (game.state === GameState.Created && isInvited) {
        return GameScore.NeedsMyAction;
    }

    if (game.state !== GameState.Complete && isMyTurn) {
        return GameScore.NeedsMyAction;
    }

    if (game.state === GameState.Complete) {
        return GameScore.SeeEndOfGame;
    }

    const g = new Game(game);

    // we need to check will nudge cause surrender here (before checking
    // if game state is Created) because you can nudge a game creator
    // who is taking too long to start the game
    if (g.willNudgeCauseSurrender() || g.isBotStalled()) {
        return GameScore.NudgeWillCauseGameAction;
    }

    if (game.state === GameState.Created) {
        return GameScore.WaitingForOtherPlayer;
    }

    if (game.isLive) {
        return GameScore.Live;
    }

    if (isTeammatesTurn(game, userId)) {
        return GameScore.TeammatesTurn;
    }

    return GameScore.WaitingForOtherPlayer;
}

export function isTeammatesTurn(game: ApiGame, userId: number): boolean {
    if (game.state === GameState.Created || game.state === GameState.Complete) {
        return false;
    }

    const userSeat = game.seats.find(
        (seatObject) => seatObject.userId === userId,
    );

    if (userSeat?.team == null) {
        return false;
    }

    return game.activeSeats.some(
        (activeSeat) => activeSeat.userId !== userId
            && activeSeat.team === userSeat.team,
    );
}

function compareGame(gameA: ApiGame, gameB: ApiGame, userId: number): number {
    const scoreA = getGameScore(gameA, userId);
    const scoreB = getGameScore(gameB, userId);

    if (scoreA !== scoreB) {
        return scoreB - scoreA;
    }

    return gameB.lastUpdated - gameA.lastUpdated;
}

export function sortActiveGames(games: ApiGame[], userId: number): ApiGame[] {
    return games.sort(
        (gameA, gameB) => compareGame(gameA, gameB, userId),
    );
}
